import React, { useState, useEffect } from 'react';
import { Table } from '../../../../components/Table';
import { useDeleteUserMutation, useDeleteUserBulkMutation, useGetUserDsQuery } from '../../../../api/users';
import { User, UserDsQueryParams } from '../../../../api/types/user';
import { ModalUsers } from '../../../../components/Modals/ModalUsers';
import { Key, TableRowSelection } from 'antd/es/table/interface';
import ArrowUp from '../../../../components/Icons/ArrowUp';
import ArrowDown from '../../../../components/Icons/ArrowDown';
import UserStatus from '../../../../components/UserStatus';
import Delete from '../../../../components/Icons/Delete';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useTableDataSourceWithHooks } from '../../../../components/Table/useTableDataPropsWithHooks';
import UserInfo from '../../../../components/UserInfo';
import InputSearch from '../../../../components/Fields/InputSearch';
import { Button, Empty, message, Typography } from 'antd';
import NoUsers from '../../../../components/Icons/NoUsers';
import NoResults from '../../../../components/Icons/NoResults';
import ModalDeleteUser from '../../../../components/Modals/ModalDeleteUser';
import { colors } from '../../../../themes/colors';
import useDebounceSearch from '../../../../hooks/useDebounceSearch';
import { CustomErrorType } from '../../../../api/types/global';

export const SchoolUsers = () => {
    const [editModal, setEditModal] = useState<{ type: string; open: boolean; id: number | null }>({
        type: 'edit',
        open: false,
        id: null
    });
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; id: number | number[] | null; name: string }>({
        open: false,
        id: null,
        name: ''
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounceSearch(search);

    const [variables, setVariables] = useState<UserDsQueryParams>({
        filter: { role: ['school_user'], search: undefined, status: undefined },
        sort_field: undefined,
        sort_direction: undefined,
        per_page: 20,
        page: 1
    });

    useEffect(() => {
        setVariables({
            ...variables,
            filter: { ...variables.filter, search: debouncedSearch }
        });
    }, [debouncedSearch]);
    const { data, isLoading, isFetching, refetch } = useGetUserDsQuery(variables);
    const [deleteUser, { isLoading: isDeleting, isError: isDeletingError, error: deletingError }] =
        useDeleteUserMutation();
    const [deleteUserBulk, { isLoading: isDeletingBulk, isError: isDeletingBulkError, error: deletingBulkError }] =
        useDeleteUserBulkMutation();
    const [messageApi, contextHolder] = message.useMessage();

    const deleteModalCloseHandler = () => {
        setDeleteModal({ open: false, id: null, name: '' });
        refetch();
    };

    useEffect(() => {
        if (isDeletingError) {
            messageApi.error('The User can not be deleted as they participate in an active tendeMatchmaking Journeyr');
        }
    }, [isDeletingError]);

    // TODO: move delete to separate file
    const handleDelete = async () => {
        try {
            await deleteUser(deleteModal.id).unwrap();
            messageApi.success(`User ${deleteModal.name} has been deleted successfully`);
        } catch (e) {
        } finally {
            setDeleteModal({ open: false, id: null, name: '' });
            refetch();
        }
    };

    const handleBulkDelete = async (ids: number[]) => {
        try {
            await deleteUserBulk({ ids }).unwrap();
            messageApi.success(`User ${deleteModal.name} has been deleted successfully`);
        } catch (e) {
            if (isDeletingBulkError) {
                messageApi.error((deletingBulkError as CustomErrorType)?.data?.message || 'Something went wrong');
            } else {
                console.error(e);
                messageApi.error('Something went wrong');
            }
        } finally {
            setDeleteModal({ open: false, id: null, name: '' });
            setSelectedRowKeys([]);
            refetch();
        }
    };

    const { tableProps } = useTableDataSourceWithHooks<User>({
        columns: [
            {
                dataIndex: 'full_name',
                title: 'Name',
                sorter: true,
                width: 350,
                sortIcon: ({ sortOrder }) => {
                    if (sortOrder === 'ascend') {
                        return <ArrowUp />;
                    }
                    if (sortOrder === 'descend') {
                        return <ArrowDown />;
                    }
                },
                render: (_, record) => (
                    <UserInfo avatarUrl={record?.avatar_thumb} fullName={record?.full_name} email={record.email} />
                )
            },
            {
                dataIndex: 'job_title',
                title: 'Job Title',
                width: 150
            },
            {
                dataIndex: 'school',
                title: 'Organisation Name',
                width: 250
            },
            {
                dataIndex: 'phone_number',
                title: 'Telephone Number',
                width: 150
            },
            {
                dataIndex: 'status',
                title: 'Status',
                width: 100,
                sorter: true,
                render: (status, record) => <UserStatus status={status} text={record.status_name} />,
                sortIcon: ({ sortOrder }) => {
                    if (sortOrder === 'ascend') {
                        return <ArrowUp />;
                    }
                    if (sortOrder === 'descend') {
                        return <ArrowDown />;
                    }
                }
            },
            {
                title: ' ',
                width: 50,
                key: 'x',
                render: (_, record) => (
                    <Delete
                        style={{ fontSize: '20px' }}
                        fill={colors.Texts_and_Icons.Disabled}
                        onClick={(event) => {
                            event.stopPropagation();
                            setDeleteModal({ open: true, id: record.id, name: record.full_name || record.email });
                        }}
                    />
                ),
                align: 'right'
            }
        ],
        rowKey: 'id',
        data: {
            rows: data?.data,
            loading: isLoading || isFetching,
            total: data?.meta?.total
        },
        paginationConfig: { defaultPageSize: 20, showSizeChanger: false, position: ['bottomCenter'] },
        onQueryVariableChange: (options) => {
            setVariables({
                ...variables,
                page: options?.page || 1,
                per_page: options?.limit || 20,
                sort_field: options?.sortField || variables.sort_field,
                sort_direction: options?.sortDirection || variables.sort_direction,
                filter: {
                    ...variables.filter
                }
            });
        }
    });

    const rowSelection: TableRowSelection<User> = {
        type: 'checkbox',
        selectedRowKeys,
        onChange: (selectedRowKeys: Key[], selectedRows: User[]) => {
            setSelectedRowKeys(selectedRowKeys);
        },
        columnWidth: 35
    };

    return (
        <>
            {contextHolder}
            <div className="header-actions">
                <InputSearch handleChange={(value) => setSearch(value)} />
                <Button
                    type="primary"
                    onClick={() => {
                        setEditModal({ type: 'create', open: true, id: null });
                    }}
                    icon={<PlusCircleOutlined />}
                >
                    Add User
                </Button>
            </div>

            <Table<User>
                {...tableProps}
                rowSelection={rowSelection}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                scroll={{ y: '70vh' }}
                handleDelete={() =>
                    setDeleteModal({ open: true, id: selectedRowKeys as number[], name: 'selected users' })
                }
                onRow={(record) => {
                    return {
                        onClick: () => {
                            if (record.status === 'pending') {
                                setEditModal({ type: 'view_pending', open: true, id: record.id });
                            } else {
                                setEditModal({ type: 'view', open: true, id: record.id });
                            }
                        }
                    };
                }}
                // TODO: move to separate component
                locale={{
                    emptyText: variables.filter?.search ? (
                        <Empty
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                gap: '24px',
                                margin: '50px 0'
                            }}
                            image={<NoResults />}
                            description={
                                <div
                                    style={{
                                        maxWidth: '390px'
                                    }}
                                >
                                    <Typography.Title level={4}>No results found</Typography.Title>
                                    <Typography.Paragraph type="secondary">
                                        Try adjusting your search to find what you are looking for
                                    </Typography.Paragraph>
                                </div>
                            }
                        />
                    ) : (
                        <Empty
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                gap: '24px',
                                margin: '50px 0'
                            }}
                            image={<NoUsers />}
                            description={
                                <div
                                    style={{
                                        maxWidth: '390px'
                                    }}
                                >
                                    <Typography.Title level={4}>No school users yet</Typography.Title>
                                    <Typography.Paragraph type="secondary">
                                        Add a new user to start to working with them
                                    </Typography.Paragraph>
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            setEditModal({ type: 'create', open: true, id: null });
                                        }}
                                        icon={<PlusCircleOutlined />}
                                    >
                                        Add User
                                    </Button>
                                </div>
                            }
                        />
                    )
                }}
            />
            {editModal.open && (
                <ModalUsers
                    openModal={editModal.open}
                    setOpenModal={setEditModal}
                    type={editModal.type}
                    userId={editModal.id}
                    refetch={() => refetch()}
                />
            )}
            <ModalDeleteUser
                open={deleteModal.open}
                onClose={deleteModalCloseHandler}
                userId={deleteModal.id}
                deleteUser={handleDelete}
                deleteUserBulk={handleBulkDelete}
                userName={deleteModal.name}
                isDeleting={isDeleting || isDeletingBulk}
            />
        </>
    );
};
